import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { OpenTabsContext } from "../../OpenTabsContext";
import { hmLightTheme } from "../../themes/hmTheme";
import CustomPaper from "../../unit/CustomPaper";
import CustomTable from "../../unit/CustomTable";
import CustomTableContainer from "../../unit/CustomTableContainer";
import formatCurrency from "../../unit/formatCurrency";
import formatDate from "../../unit/formatDate";
import useNotify from "../../unit/useNotify";
const TableCellRight = styled(TableCell)({ textAlign: "right" });

const CustomAttentionField = ({ ...props }) => {
  const transferFailedNumber = props.iTransferFailedNumber;
  const insufficientBalanceCheckNumber = props.iInsufficientBalanceCheckNumber;
  const transferFatalErrorNumber = props.iTransferFatalErrorNumber;

  let textcolor = "textSecondary";

  let errorValue = [];
  if (transferFailedNumber >= 2) {
    errorValue.push("Transfer Failed");
  }
  if (insufficientBalanceCheckNumber >= 2) {
    errorValue.push("Insufficient Balance");
  }
  if (transferFatalErrorNumber >= 1) {
    errorValue.push("Funding Source Removed");
  }
  return (
    <Typography sx={{ mb: 1 }}>
      {errorValue.map((item, index) => (
        <span key={index}>
          <br />
          {item}
        </span>
      ))}
    </Typography>
  );
};
const ApplicationIDLinkField = ({ ...props }) => {
  const { openGlobalTabs } = useContext(OpenTabsContext);
  const navigate = useNavigate();
  const { successNotify, errorNotify, infoNotify } = useNotify();

  const { paramAppId, loannum } = props;
  const handleClick = (id: any, loannum: any) => {
    if (openGlobalTabs.length >= 6) {
      if (openGlobalTabs.includes("app-" + id + "-" + loannum)) {
        navigate(`/applicationSummary/` + id + "?loannum=" + loannum);
      } else {
        errorNotify("Cannot open more than 6 tabs.");
        return false;
      }
    } else {
      navigate(`/applicationSummary/` + id + "?loannum=" + loannum);
    }
  };

  return (
    <Link
      component="span"
      onClick={() => handleClick(paramAppId, loannum)}
      sx={{
        cursor: "pointer",
        textDecoration: "none",
        fontFaimily: hmLightTheme?.palette?.numberFontFamily,
      }}
    >
      {paramAppId}
    </Link>
  );
};

const LoanNumberLinkField = ({ ...props }) => {
  const { openGlobalTabs } = useContext(OpenTabsContext);
  const navigate = useNavigate();
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const { paramAppId, loannum } = props;
  const handleClick = (id: any, loannum: any) => {
    if (openGlobalTabs.length >= 6) {
      if (openGlobalTabs.includes("loan-" + id + "-" + loannum)) {
        navigate(`/loanSummary/` + id + `?loannum=` + loannum);
      } else {
        errorNotify("Cannot open more than 6 tabs.");
        return false;
      }
    } else {
      navigate(`/loanSummary/` + id + `?loannum=` + loannum);
    }
  };

  return (
    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
      <Link
        component="span"
        onClick={() => handleClick(paramAppId, loannum)}
        sx={{
          cursor: "pointer",
          textDecoration: "none",
          fontFamily: hmLightTheme?.palette?.numberFontFamily,
          "&.RaLink-link": {
            color: "#1976d2",
          },
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {loannum}
      </Link>
    </Typography>
  );
};

const CustomSummaryLoanList = ({ ...props }) => {
  const userId = props.userId;
  const loans = props.loans;
  if (loans == undefined || loans == null) {
    return <Typography>N/A Loans</Typography>;
  }

  return (
    <>
      <CustomTableContainer component={CustomPaper}>
        <CustomTable stickyHeader id="table-customer-summary-loan-list">
          <TableHead>
            <TableRow>
              <TableCell className="hm-table-header">Loan #</TableCell>
              <TableCell className="hm-table-header">Institution</TableCell>
              <TableCell className="hm-table-header">Product</TableCell>
              <TableCell sx={{ minWidth: "120px" }} className="hm-table-header">
                Note Date
              </TableCell>
              <TableCell sx={{ minWidth: "120px" }} className="hm-table-header">
                Maturity Date
              </TableCell>
              {/* <TableCell>Original Principal Amount</TableCell> */}
              <TableCell align="right" className="hm-table-header">
                Remaining Principal Amount
              </TableCell>
              <TableCell className="hm-table-header">
                Payment Frequency
              </TableCell>
              <TableCell sx={{ minWidth: "120px" }} className="hm-table-header">
                Loan Status
              </TableCell>
              <TableCell className="hm-table-header">Attention</TableCell>
              <TableCell className="hm-table-header">Reset History</TableCell>
              {/* <TableCell>AID</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {loans == undefined || loans.length == 0 || loans == null ? (
              <TableRow>
                <TableCell colSpan={8}>No Loans </TableCell>
              </TableRow>
            ) : (
              loans.map((item: any) => (
                <TableRow
                  key={item.iId + " " + item.sLoanNum + " " + item.dtInsert}
                >
                  <TableCell>
                    <LoanNumberLinkField
                      paramAppId={item.iApplicationId}
                      loannum={item.sLoanNum}
                    />
                  </TableCell>
                  <TableCell>
                    {item.sBankName == null ? "N/A" : item.sBankName}
                  </TableCell>
                  <TableCell>{item.sProductName}</TableCell>
                  <TableCell>{formatDate(item.dNoteDate)}</TableCell>
                  <TableCell>{formatDate(item.dMaturityDate)}</TableCell>
                  {/* <TableCell>{item.sOriginalNoteAmount}</TableCell> */}
                  <TableCell className="amountText">
                    {formatCurrency(item.sActivePrincipalBalance)}
                  </TableCell>
                  <TableCell>{item.sPaymentFrequency}</TableCell>
                  <TableCell>{item.curStatus}</TableCell>
                  <TableCell>
                    <CustomAttentionField
                      label="Attention"
                      iTransferFailedNumber={item.iTransferFailedNumber}
                      iInsufficientBalanceCheckNumber={
                        item.iInsufficientBalanceCheckNumber
                      }
                      iTransferFatalErrorNumber={item.iTransferFatalErrorNumber}
                    />
                  </TableCell>
                  <TableCell>{item.allModifyNum}</TableCell>
                  {/* <TableCell>{item.iApplicationId}</TableCell> */}
                </TableRow>
              ))
            )}
          </TableBody>
        </CustomTable>
      </CustomTableContainer>
    </>
  );
};

export default CustomSummaryLoanList;
